import { Button, Tooltip } from "@mantine/core"
import { showNotification } from "@dehub/theme"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import { OrderApi } from "../../apis"
import type { IOrder } from "../../models/Order"
import { usePermission } from "../permission"
import { ReasonTooltip } from "../ui"
import { CancelConfirmedOrderModal } from "./CancelConfirmedOrderModal"

type Props = {
  order: IOrder
  onSuccess?: () => void
}

export function CancelConfirmedOrderButton ({ order, onSuccess }: Props) {
  const { t } = useTranslation(["supplierSales", "common"])
  const ORD_CANCEL = usePermission("ORD_CANCEL")
  const [opened, setOpened] = useState(false)
  const [loading, setLoading] = useState(false)

  const statusApplies = order.orderStatus === "AUTHORIZED"
  const enabled = ORD_CANCEL.hasAccess && statusApplies

  const { data } = useSWR(
    enabled ? ["order.cancellability", order.id] : null,
    async () => await OrderApi.getCancellability(order.id as string)
  )

  if (!statusApplies) return null
  if (!ORD_CANCEL.hasAccess) {
    // Stays visible, aria-disabled, with «Эрх хэрэгтэй: ORD_CANCEL».
    return (
      <ReasonTooltip reason={ORD_CANCEL.reason}>
        <Button variant="default">{t("supplierSales:cancelConfirmed.button")}</Button>
      </ReasonTooltip>
    )
  }

  const cancellable = data?.cancellable === true
  const reasons = data?.reasons ?? []
  const reasonText = reasons.length > 0
    ? reasons.map((code) => t(`supplierSales:cancelConfirmed.reasons.${code}`)).join("; ")
    : t("supplierSales:cancelConfirmed.disabled")

  const orderCode = order.salesCode || order.purchaseCode || order.id || "—"
  const invoiceCodes = [
    (order as { proformaInvoice?: { refCode?: string } }).proformaInvoice?.refCode,
    order.proformaInvoiceId,
    order.invoiceId
  ].filter((v, i, a): v is string => Boolean(v) && a.indexOf(v) === i)

  const dnCount = Array.isArray((order as { deliveryNotes?: unknown[] }).deliveryNotes)
    ? ((order as { deliveryNotes?: unknown[] }).deliveryNotes?.length ?? 0)
    : ((order as { deliveryNote?: unknown }).deliveryNote ? 1 : 0)

  const handleConfirm = async (reason: string) => {
    setLoading(true)
    try {
      await OrderApi.cancelConfirmed(order.id as string, reason)
      showNotification({
        message: t("supplierSales:cancelConfirmed.success"),
        color  : "green"
      })
      setOpened(false)
      onSuccess?.()
    } catch {
      /* HttpRequest / HttpHandler surfaces the error */
    } finally {
      setLoading(false)
    }
  }

  const button = (
    <Button
      variant="default"
      disabled={!cancellable}
      onClick={() => setOpened(true)}
    >
      {t("supplierSales:cancelConfirmed.button")}
    </Button>
  )

  return (
    <>
      {cancellable ? button : (
        <Tooltip label={reasonText} multiline maw={280}>
          <span>{button}</span>
        </Tooltip>
      )}
      <CancelConfirmedOrderModal
        opened={opened}
        onClose={() => setOpened(false)}
        onConfirm={handleConfirm}
        loading={loading}
        orderCode={String(orderCode)}
        deliveryNoteCount={dnCount}
        invoiceCodes={invoiceCodes}
      />
    </>
  )
}
